import { useEffect, useState } from "react";
import { useApi, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Filter, X } from "lucide-react";

const EMPTY = {
  prov_lc_no: "", prov_lc_name: "", province: "", type: "",
  comp_ticket_count: "", contact_title: "",
  contact_first_name: "", contact_surname: "", contact_email: "",
};

function hasExtendedDetails(e) {
  return (
    (e.comp_ticket_count != null && e.comp_ticket_count !== "") ||
    e.contact_title || e.contact_first_name || e.contact_surname || e.contact_email
  );
}

function extendedLine(e) {
  const bits = [];
  if (e.comp_ticket_count != null && e.comp_ticket_count !== "") {
    bits.push(`Comp tickets: ${e.comp_ticket_count}`);
  }
  const person = [e.contact_title, e.contact_first_name, e.contact_surname]
    .filter(Boolean).join(" ").trim();
  if (person) bits.push(person);
  if (e.contact_email) bits.push(e.contact_email);
  return bits.join(" · ");
}

export default function AdminProvinceLCPage() {
  const api = useApi();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {entry_id, ...} for edit, {} for create
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // When true, hide any row where `comp_ticket_count` is blank (null / undefined).
  // Icon flips between Filter (off) and a red cross (on) so admins see
  // the current state at a glance.
  const [ticketFilterOn, setTicketFilterOn] = useState(false);

  const visibleEntries = ticketFilterOn
    ? entries.filter((e) => e.comp_ticket_count != null && e.comp_ticket_count !== "")
    : entries;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api("/admin/province-lc");
      if (r.ok) {
        const d = await r.json();
        setEntries(d.entries || []);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => { setEditing({}); setForm(EMPTY); };
  const openEdit = (row) => { setEditing(row); setForm({
    prov_lc_no: row.prov_lc_no, prov_lc_name: row.prov_lc_name,
    province: row.province, type: row.type,
    comp_ticket_count: row.comp_ticket_count ?? "",
    contact_title: row.contact_title || "",
    contact_first_name: row.contact_first_name || "",
    contact_surname: row.contact_surname || "",
    contact_email: row.contact_email || "",
  }); };

  const submit = async () => {
    if (!form.prov_lc_no.trim() || !form.prov_lc_name.trim() ||
        !form.province.trim() || !form.type.trim()) {
      toast.error("All four fields are required.");
      return;
    }
    setBusy(true);
    try {
      const isEdit = !!editing?.entry_id;
      // Only send extended fields when the user has typed something —
      // empty strings clear the field on the server, so pass "" only
      // if it was previously populated and the admin blanked it.
      const payload = {
        prov_lc_no: form.prov_lc_no,
        prov_lc_name: form.prov_lc_name,
        province: form.province,
        type: form.type,
        comp_ticket_count: form.comp_ticket_count === ""
          ? null
          : Number(form.comp_ticket_count),
        contact_title: form.contact_title,
        contact_first_name: form.contact_first_name,
        contact_surname: form.contact_surname,
        contact_email: form.contact_email,
      };
      const r = await api(
        isEdit ? `/admin/province-lc/${editing.entry_id}` : "/admin/province-lc",
        { method: isEdit ? "PATCH" : "POST", body: JSON.stringify(payload) },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Save failed"); return; }
      toast.success(isEdit ? "Entry updated" : "Entry added");
      setEditing(null);
      await load();
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      const r = await api(`/admin/province-lc/${confirmDelete.entry_id}`, { method: "DELETE" });
      if (!r.ok) { const d = await r.json().catch(()=>({})); toast.error(d.detail || "Delete failed"); return; }
      toast.success("Entry deleted");
      setConfirmDelete(null);
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Other Provinces/Districts Complimentary Ticket Management"
        subtitle="Province &amp; District master list - Add, Edit and Delete"
        testId="province-lc-header"
      />

      <Card className="border-2 border-red-500/60 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm text-muted-foreground">
              {loading
                ? "Loading…"
                : ticketFilterOn
                  ? `${visibleEntries.length} of ${entries.length} entries (filtered)`
                  : `${entries.length} entries`}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setTicketFilterOn((v) => !v)}
              data-testid="plc-filter-toggle"
              aria-pressed={ticketFilterOn}
            >
              {ticketFilterOn
                ? <X size={16} className="mr-2 text-red-600" />
                : <Filter size={16} className="mr-2" />}
              Filter Active
            </Button>
            <Button onClick={openCreate} data-testid="plc-new">
              <Plus size={16} className="mr-2" /> New entry
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-3 text-red-600 font-semibold">#</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Prov L/C No</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Prov L/C Name</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Province</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Type</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((e, i) => (
                <tr key={e.entry_id} className="border-b hover:bg-red-50" data-testid={`plc-row-${e.entry_id}`}>
                  <td className="py-2 pr-3 text-muted-foreground align-top">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono align-top">{e.prov_lc_no}</td>
                  <td className="py-2 pr-3 align-top">
                    <div>{e.prov_lc_name}</div>
                    {hasExtendedDetails(e) && (
                      <div className="text-xs text-muted-foreground mt-1" data-testid={`plc-extended-${e.entry_id}`}>
                        {extendedLine(e)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 align-top">{e.province}</td>
                  <td className="py-2 pr-3 align-top">{e.type}</td>
                  <td className="py-2 pr-3 flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)}
                            data-testid={`plc-edit-${e.entry_id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(e)}
                            data-testid={`plc-delete-${e.entry_id}`}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && visibleEntries.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground italic">
                  {ticketFilterOn
                    ? "No entries have a complimentary ticket count yet."
                    : 'No entries yet. Add the first one using "New entry".'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !busy) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.entry_id ? "Edit entry" : "New entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Prov L/C No</Label>
              <Input data-testid="plc-form-no" value={form.prov_lc_no}
                     onChange={(e) => setForm({ ...form, prov_lc_no: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Prov L/C Name</Label>
              <Input data-testid="plc-form-name" value={form.prov_lc_name}
                     onChange={(e) => setForm({ ...form, prov_lc_name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Province</Label>
              <Input data-testid="plc-form-province" value={form.province}
                     onChange={(e) => setForm({ ...form, province: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Input data-testid="plc-form-type" value={form.type}
                     onChange={(e) => setForm({ ...form, type: e.target.value })} />
            </div>

            <div className="mt-3 pt-3 border-t">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Optional details
              </div>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Complimentary ticket count</Label>
                  <Input type="number" min="0" step="1"
                         data-testid="plc-form-ticket-count"
                         value={form.comp_ticket_count}
                         onChange={(e) => setForm({ ...form, comp_ticket_count: e.target.value })}
                         placeholder="Leave blank if unset" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label>Title</Label>
                    <Input data-testid="plc-form-contact-title" value={form.contact_title}
                           onChange={(e) => setForm({ ...form, contact_title: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>First name</Label>
                    <Input data-testid="plc-form-contact-first" value={form.contact_first_name}
                           onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Surname</Label>
                    <Input data-testid="plc-form-contact-surname" value={form.contact_surname}
                           onChange={(e) => setForm({ ...form, contact_surname: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Contact email</Label>
                  <Input type="email" data-testid="plc-form-contact-email"
                         value={form.contact_email}
                         onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="plc-form-save">
              {busy ? "Saving…" : (editing?.entry_id ? "Save changes" : "Add entry")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !busy) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <span className="font-medium text-foreground">{confirmDelete?.prov_lc_no}</span>
              {" "}({confirmDelete?.prov_lc_name}) from the master list. The remaining entries keep their existing order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); del(); }} disabled={busy}
                               data-testid="plc-delete-confirm">
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
